import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import cpgSquare from './../../assets/images/cpg-shapes-square.svg';
import cpgTriangle from './../../assets/images/cpg-shapes-triangle.svg';
import directChart from './../../assets/images/cpg-direct-chart.svg';
import './../cpg.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function CPGPlanning() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="cpg-planning">

        <p id="s1" className="intro" data-aos="fade-up">Discover how businesses in the consumer packaged goods (CPG) sector plan and manage direct mail marketing.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Expertise</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="69" aria-hidden="true">0</span><span class="screen-reader-text">69</span>%</span>
                </span>

                <p>Advanced knowledge</p>
                <p>Along with financial businesses, CPG companies report having higher expertise in direct mail than any other industry.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Even if you don’t feel fully confident incorporating direct mail into your campaigns, it’s easy to get started. Watch a quick <strong><a href="/../a-beginners-guide-to-direct-mail-a-5-part-video-series/">five-part video tutorial</a></strong> to learn how to pick the best mail format for your goals, define your target audience, create a mailing list, create a mailpiece and measure results.</p>
              </div>
            </div>
          </div>

          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Management of Direct Mail</h3>
                <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                  <div className='progressbar-item'>
                    <span className='label'>Performance measurement <strong>in-house + agency</strong></span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="49"><span class="percentage" data-to="49" aria-hidden="true">0</span><span class="screen-reader-text">49</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Sorting, assembling and mailing <strong>in-house + agency</strong></span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="60"><span class="percentage" data-to="60" aria-hidden="true">0</span><span class="screen-reader-text">60</span>%</div>
                    </div>
                  </div>
                </div>
                <p>Out of all the industries surveyed, CPG companies are most likely to share responsibilities for direct mail performance measurement and sorting, assembling and mailing with an outside agency.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Working with a partner can be a great option for brands less confident with direct mail. Before you start your search, first focus on clearly defining your goals—are you looking to increase response rates? Raise awareness? Then look for agencies specializing in the right areas, and be sure to assess their customer service options.</p>
              </div>
            </div>
            <div className='inner-cols'>
              <div className='inner-col-item'>
                <div className='personalized-box secondary align-to-text'>
                  <h3>CPG companies often share direct mail tasks with an outside agency.</h3>
                  <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                    <div className='triangle triangle-1'>
                      <img ref={triangle1Ref} src={cpgTriangle} alt="" />
                    </div>
                    <div className='square square-1'>
                      <img ref={square1Ref} src={cpgSquare} alt="" />
                    </div>
                    <div className='triangle triangle-2'>
                      <img ref={triangle2Ref} src={cpgTriangle} alt="" />
                    </div>
                    <div className='square square-2'>
                      <img ref={square2Ref} src={cpgSquare} alt="" />
                    </div>
                  </div>
                </div>
              </div>
              <div className='inner-col-item'>
                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Source for Direct Mail Targets</h3>
                    <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                      <div className='progressbar-item'>
                        <span className='label'>Both in-house and outside mailing lists</span>
                        <div className='progress-bar size-small'>
                          <div className='progress' data-fill="54"><span class="percentage" data-to="54" aria-hidden="true">0</span><span class="screen-reader-text">54</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>In-house mailing lists</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="43"><span class="percentage" data-to="43" aria-hidden="true">0</span><span class="screen-reader-text">43</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>Outside mailing lists</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="3"><span class="percentage" data-to="3" aria-hidden="true">0</span><span class="screen-reader-text">3</span>%</div>
                        </div>
                      </div>
                    </div>
                    <p>CPG businesses are the least likely to have in-house (“homegrown”) mailing lists, instead opting to share mailing list responsibility evenly with outside agencies. As with businesses in other industries, CPG companies rarely work primarily with outside lists (broker lists).</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                     <p>If you’re working with in-house mailing lists, it’s important to keep them up to date. Scrub lists regularly to ensure addresses are up to date (necessary in order to comply with the <strong><a href="https://postalpro.usps.com/address-quality/moveupdate?_gl=1*k5nvmi*_gcl_au*MzY5MjA5NzAxLjE2OTIxMjkyMzI.*_ga*MTMxMzIzMzYwMS4xNjkyMTI5MjMy*_ga_3NXP3C8S9V*MTY5MzMzNDc5OC4yOC4xLjE2OTMzMzU0NzQuMC4wLjA.&_ga=2.100408669.257108239.1693241829-1313233601.1692129232" target="_blank" className="external-link">Move Update standard</a></strong>, which requires all mailing lists to have been updated within the last 95 days), and that there are no duplicates.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
